import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import AppEnvironment from '../../../components/enums/appEnvironment.enum';
import LangEnum from '../../../lang/LangEnum';
import { brazilCountry } from '../../../lib/countries';
import api, { restApi } from '../../../services/api';
import { clearFederatedSessionFlag } from '../../../services/modules/sso/sso.utils';
import snackbarStore from '../../../stores/Snackbar.store';
import { onlyNumbersAndLetters, unmaskCpfCnpj } from '../../../utils/validators';
import { StorageEnum } from '../../context/storage';
import { UserMainStorage, useAuthContext } from '../../login/AuthContext';
import { LoginCredentials } from '../../login/hook/useAuthLogin';
import { useTranslation } from '../../translation/TranslationContext';
import { signUpRepository } from '../signUp.repository';
import { CreateAccountProps, CreateUserContextPayload } from '../types/createAccount.types';
import {
  CreateContextLoginFormValues,
  buildCreateContextLoginSchema,
} from '../utils/validator/createContextLogin.schema';

const buildExistentUserStore = (store: CreateAccountProps['store']) => {
  const formattedCpfCnpj =
    store?.location === brazilCountry.code ? unmaskCpfCnpj(store?.cpfcnpj) : store?.cpfcnpj;

  return {
    ...store,
    cpfcnpj: store?.cpfcnpj ? formattedCpfCnpj : null,
    phone: store?.phone ? store.phone.slice(store.countryCode?.length) : undefined,
    address: {
      ...store?.address,
      zipCode: store?.address?.zipCode ? onlyNumbersAndLetters(store?.address?.zipCode) : undefined,
    },
  };
};

export const useCreateContextLogin = ({ store }: CreateAccountProps) => {
  const { handleUserLogin, loginContext, redirectPath } = useAuthContext();
  const history = useHistory();
  const { translate } = useTranslation();
  const { handleMessage } = snackbarStore;

  const loginContextFromEvents = loginContext?.slug.split('/')[0] === 'events';
  const wrongCredentialsMessage = translate(LangEnum.LOGIN_WORNG_CREDENCIALS);

  const form = useForm<CreateContextLoginFormValues>({
    resolver: zodResolver(buildCreateContextLoginSchema(translate)),
    defaultValues: {
      username: '',
      password: '',
    },
  });

  const handleLogin = async ({ username, password }: LoginCredentials) => {
    try {
      const response = await api.login({ username, password });
      if (response.ok && response.data) {
        const { data } = response;
        api.setToken(data.access_token);
        clearFederatedSessionFlag();

        return {
          id: data.id,
          name: data.name,
          surname: data.surname,
          email: data.email,
          profileUrl: data.profileUrl,
          username: data.username,
          access_token: data.access_token,
          ok: true,
        };
      }
      return { ok: false, message: wrongCredentialsMessage };
    } catch (_e) {
      return { ok: false, message: wrongCredentialsMessage };
    }
  };

  const afterLogin = (data: UserMainStorage) => {
    handleUserLogin(data);
    history.push(redirectPath !== '/' ? redirectPath : `/${loginContext?.slug || '/'}`);
  };

  const acceptOrCreateInvite = async () => {
    if (loginContext && !loginContextFromEvents) {
      await restApi.post(`/brands/${loginContext.id}/connection-or-invite`);
    }
  };

  const handleDirectLogin = async (teamId: string, token: string) => {
    const appEnvironment = AppEnvironment.STORE;

    api.setTeam(teamId);
    api.setAppEnvironment(appEnvironment);

    const data: UserMainStorage = {
      [StorageEnum.SESSION_ID]: token,
      [StorageEnum.TEAM_ID]: teamId,
      [StorageEnum.ADMIN_TEAM_ID]: '',
      [StorageEnum.BRAND_ID]: '',
      [StorageEnum.APP_ENVIRONMENT]: appEnvironment,
    };

    await acceptOrCreateInvite();

    afterLogin(data);
  };

  const doLogin = async (credentials: CreateContextLoginFormValues, teamId: string) => {
    const response = await handleLogin(credentials);
    if (response.ok) {
      handleMessage({
        type: 'success',
        message: translate(LangEnum.LOGIN_WELCOME_NAME, { name: response.name ?? '' }),
      });

      await handleDirectLogin(teamId, response.access_token);
    } else {
      handleMessage({ type: 'error', message: response.message });
    }
    return response;
  };

  const submit = form.handleSubmit(async (credentials) => {
    const repository = signUpRepository();

    const response = await repository.createUserContext({
      ...credentials,
      store: buildExistentUserStore(store),
      fromBrandId: !loginContextFromEvents ? loginContext?.id : undefined,
    } as unknown as CreateUserContextPayload);

    if (!response.ok) {
      handleMessage({
        type: 'error',
        message:
          response.data?.statusCode === 401
            ? wrongCredentialsMessage
            : response.data?.message || translate(LangEnum.LOGIN_CURRENTLY_UNAVAILABLE),
      });
    }

    if (response.ok && response.data) {
      await doLogin(credentials, response.data.id);
    }

    form.reset({ username: credentials.username, password: '' });
  });

  return {
    form,
    submit,
    isSubmitting: form.formState.isSubmitting,
  };
};
